/**
 * Value lists of the audio-batch (逐字稿) lifecycle that the API, the wire
 * contract and the web all speak.
 *
 * Here rather than in `@kiao-voice/api-contract` because apps/api keeps a zero
 * runtime dependency on that package (machine-enforced by
 * `apps/api/eslint.config.js`), and the API's validators, models and queries
 * are callers too. `@kiao-voice/api-contract/batch-transcription` re-exports
 * the types so client import paths stay where they were.
 */

/**
 * `batch_jobs.status`. `anonymous_trials.status` mirrors it (the trial row is
 * the public face of one batch, and the worker copies the batch's outcome onto
 * it), so both columns take their values from this one list.
 */
export const AUDIO_BATCH_STATUSES = [
  'pending',
  'processing',
  'completed',
  'failed',
  'cancelled',
] as const
export type AudioBatchStatus = (typeof AUDIO_BATCH_STATUSES)[number]

/**
 * Batch statuses no worker will move again. A `pending`/`processing` batch is
 * still owned by the queue, however old it is.
 */
export const TERMINAL_AUDIO_BATCH_STATUSES = [
  'completed',
  'failed',
  'cancelled',
] as const satisfies readonly AudioBatchStatus[]

/**
 * `video_transcription_jobs.status` values no worker will move again. The
 * in-flight counterpart is `NON_TERMINAL_ITEM_STATUSES` on the API model.
 */
export const TERMINAL_AUDIO_BATCH_ITEM_STATUSES = ['completed', 'failed'] as const

/**
 * The coarse per-file status the history list and the detail doc-head show:
 * every in-flight item status collapses to `processing`.
 */
export const AUDIO_BATCH_DISPLAY_STATUSES = [
  'completed',
  'processing',
  'failed',
  'cancelled',
] as const
export type AudioBatchDisplayStatus = (typeof AUDIO_BATCH_DISPLAY_STATUSES)[number]

/**
 * Whether the retention window takes the transcript too (`purge_all`), or only
 * the audio (`retain_transcript`). Stamped on the batch at submission time
 * from the plan then in force.
 */
export const TRANSCRIPT_RETENTION_POLICIES = ['purge_all', 'retain_transcript'] as const
export type TranscriptRetentionPolicy = (typeof TRANSCRIPT_RETENTION_POLICIES)[number]

/** Whether a batch item's source material is audio-only or has video (playback surface, ffmpeg extraction). */
export const MEDIA_KINDS = ['audio', 'video'] as const
export type MediaKind = (typeof MEDIA_KINDS)[number]
